import { createApiClient } from '../../shared/http/createApiClient.js';

const client = createApiClient({ auth: true });

export function mountSqlEditor(root) {

  const editor    = root.querySelector('#sq-editor');
  const gutter    = root.querySelector('#sq-gutter');
  const typeBadge = root.querySelector('#sq-type-badge');
  const statusEl  = root.querySelector('#sq-status');
  const runBtn    = root.querySelector('#sq-run-btn');
  const resultsBody   = root.querySelector('#sq-results-body');
  const resultsInfo   = root.querySelector('#sq-results-info');
  const resultsActions = root.querySelector('#sq-results-actions');
  const editorArea    = root.querySelector('#sq-editor-area');
  const dragHandle    = root.querySelector('#sq-drag-handle');
  const treeEl        = root.querySelector('#sq-tree');

  let lastSelectResult = null; // { columns, rows } for CSV export

  // ── Line numbers ────────────────────────────────────────────────────
  function updateGutter() {
    const lines = (editor.value.match(/\n/g) || []).length + 1;
    let html = '';
    for (let i = 1; i <= lines; i++) {
      html += `<span class="sq-gutter-line">${i}</span>`;
    }
    gutter.innerHTML = html;
    // sync scroll
    gutter.scrollTop = editor.scrollTop;
  }

  editor.addEventListener('input', () => { updateGutter(); updateTypeBadge(); });
  editor.addEventListener('scroll', () => { gutter.scrollTop = editor.scrollTop; });

  // ── Type badge ─────────────────────────────────────────────────────
  const BADGE_CLASSES = {
    select: 'sq-badge-select', insert: 'sq-badge-insert',
    update: 'sq-badge-update', delete: 'sq-badge-delete',
    with:   'sq-badge-with',
  };

  function updateTypeBadge() {
    const kw = firstKeyword(editor.value);
    typeBadge.className = 'sq-badge ' + (BADGE_CLASSES[kw] || 'sq-badge-other');
    typeBadge.textContent = kw ? kw.toUpperCase() : '—';
  }

  function firstKeyword(sql) {
    const stripped = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
    const m = stripped.trim().match(/^(\w+)/);
    return m ? m[1].toLowerCase() : '';
  }

  // ── Tab key inserts spaces ──────────────────────────────────────────
  editor.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = editor.selectionStart;
      const end   = editor.selectionEnd;
      editor.value = editor.value.slice(0, start) + '  ' + editor.value.slice(end);
      editor.selectionStart = editor.selectionEnd = start + 2;
      updateGutter();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      sqRun();
    }
  });

  // ── Schema browser: dynamic table list ─────────────────────────────
  async function loadSchema() {
    treeEl.innerHTML = '<div class="sq-schema-loading">Loading…</div>';
    try {
      const data = await client.get('dashboard/admin/tables');
      renderSchema(data.tables || []);
    } catch {
      treeEl.innerHTML = '<div class="sq-schema-loading" style="color:var(--red)">Failed to load tables</div>';
    }
  }

  function renderSchema(tables) {
    if (!tables.length) {
      treeEl.innerHTML = '<div class="sq-schema-loading">No tables found</div>';
      return;
    }
    const bySchema = Object.create(null);
    tables.forEach(({ schema, name }) => {
      (bySchema[schema] = bySchema[schema] || []).push(name);
    });
    let html = '';
    Object.keys(bySchema).sort().forEach(schema => {
      html += `<div class="sq-schema-label">${esc(schema)}</div>`;
      bySchema[schema].forEach(name => {
        const full = [schema, name].map(part => `"${part.replace(/"/g, '""')}"`).join('.');
        html += `<button class="sq-table-item" data-table="${esc(full)}"><i class="ti ti-table"></i><span>${esc(name)}</span></button>`;
      });
    });
    treeEl.innerHTML = html;
    treeEl.querySelectorAll('.sq-table-item').forEach(btn => {
      btn.addEventListener('click', () => {
        const tbl = btn.getAttribute('data-table');
        editor.value = `SELECT *\nFROM ${tbl}\nLIMIT 50;`;
        editor.focus();
        updateGutter();
        updateTypeBadge();
      });
    });
  }

  window.sqRefreshSchema = loadSchema;

  // ── Clear ───────────────────────────────────────────────────────────
  window.sqClear = function () {
    editor.value = '';
    lastSelectResult = null;
    resultsBody.innerHTML = '<div class="sq-empty"><i class="ti ti-terminal-2"></i><span>Run a query to see results</span></div>';
    resultsInfo.innerHTML = '<i class="ti ti-table"></i>Results';
    resultsActions.style.display = 'none';
    statusEl.className = 'sq-status';
    statusEl.textContent = '';
    root.querySelector('#sq-csv-btn') && (root.querySelector('#sq-csv-btn').style.display = 'none');
    updateGutter();
    updateTypeBadge();
    editor.focus();
  };

  // ── Run ─────────────────────────────────────────────────────────────
  window.sqRun = async function () {
    const sql = editor.value.trim();
    if (!sql) return;

    runBtn.disabled = true;
    statusEl.className = 'sq-status';
    statusEl.innerHTML = '<span class="spinner-sm"></span> Running…';
    resultsBody.innerHTML = '<div class="sq-spinner"><span class="spinner-sm"></span>Executing query…</div>';
    resultsInfo.innerHTML = '<i class="ti ti-table"></i>Results';
    resultsActions.style.display = 'none';
    lastSelectResult = null;

    const t0 = Date.now();
    try {
      const data = await client.post('dashboard/admin/query', { sql });
      const elapsed = ((Date.now() - t0) / 1000).toFixed(2);

      if (data.type === 'select') {
        lastSelectResult = data;
        renderTable(data);
        resultsInfo.innerHTML = `<i class="ti ti-table"></i>${data.rowCount.toLocaleString()} row${data.rowCount !== 1 ? 's' : ''} · ${elapsed}s`;
        resultsActions.style.display = 'flex';
        statusEl.className = 'sq-status ok';
        statusEl.innerHTML = `<i class="ti ti-check"></i>${data.rowCount.toLocaleString()} rows returned in ${elapsed}s`;
      } else {
        renderDml(data, elapsed);
        statusEl.className = 'sq-status ok';
        statusEl.innerHTML = `<i class="ti ti-check"></i>${esc(data.message)} (${elapsed}s)`;
        // refresh schema after DDL that might add/remove tables
        const ddlKw = ['create', 'drop', 'alter'];
        if (ddlKw.includes(firstKeyword(sql))) loadSchema();
      }
    } catch (err) {
      renderError(err.message || String(err));
      statusEl.className = 'sq-status err';
      statusEl.innerHTML = `<i class="ti ti-alert-circle"></i>${esc(truncate(err.message, 80))}`;
    } finally {
      runBtn.disabled = false;
    }
  };

  function truncate(s, n) {
    return s && s.length > n ? s.slice(0, n) + '…' : s;
  }

  // ── Render SELECT table ─────────────────────────────────────────────
  function renderTable({ columns, rows }) {
    if (!columns.length && !rows.length) {
      resultsBody.innerHTML = '<div class="sq-empty"><i class="ti ti-mood-empty"></i><span>Query returned no rows</span></div>';
      return;
    }

    const isNumeric = columns.map((_, ci) =>
      rows.every(r => r[ci] === null || typeof r[ci] === 'number' || /^-?\d+(\.\d+)?$/.test(String(r[ci])))
    );

    let html = '<table class="sq-table"><thead><tr>';
    columns.forEach(c => { html += `<th>${esc(c)}</th>`; });
    html += '</tr></thead><tbody>';

    rows.forEach(row => {
      html += '<tr>';
      row.forEach((cell, ci) => {
        if (cell === null) {
          html += '<td class="sq-null">NULL</td>';
        } else {
          const cls = isNumeric[ci] ? ' class="sq-num"' : '';
          html += `<td${cls} title="${esc(String(cell))}">${esc(String(cell))}</td>`;
        }
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    resultsBody.innerHTML = html;
  }

  // ── Render DML result ───────────────────────────────────────────────
  function renderDml({ affected, message }) {
    resultsBody.innerHTML = `
      <div class="sq-dml-result">
        <div class="sq-dml-icon"><i class="ti ti-check"></i></div>
        <div class="sq-dml-msg">${esc(message)}</div>
        <div class="sq-dml-sub">${affected} row${affected !== 1 ? 's' : ''} modified in the database</div>
      </div>`;
  }

  // ── Render error ────────────────────────────────────────────────────
  function renderError(msg) {
    resultsBody.innerHTML = `
      <div style="padding:12px">
        <div class="sq-error-label"><i class="ti ti-alert-circle"></i>Error</div>
        <div class="sq-error">${esc(msg)}</div>
      </div>`;
    resultsInfo.innerHTML = '<i class="ti ti-alert-circle" style="color:var(--red)"></i>Error';
  }

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── CSV export ──────────────────────────────────────────────────────
  window.sqExportCsv = function () {
    if (!lastSelectResult) return;
    const { columns, rows } = lastSelectResult;
    const csvRows = [columns, ...rows].map(r =>
      r.map(cell => {
        if (cell === null) return '';
        const s = String(cell);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      }).join(',')
    );
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `query-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Drag-to-resize editor / results ────────────────────────────────
  let dragging = false;
  let startY = 0;
  let startH = 0;

  dragHandle.addEventListener('mousedown', (e) => {
    dragging = true;
    startY = e.clientY;
    startH = editorArea.offsetHeight;
    dragHandle.classList.add('dragging');
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  });

  function onMouseMove(e) {
    if (!dragging) return;
    const delta = e.clientY - startY;
    const newH  = Math.max(80, Math.min(startH + delta, window.innerHeight - 200));
    editorArea.style.height = newH + 'px';
  }

  function onMouseUp() {
    if (!dragging) return;
    dragging = false;
    dragHandle.classList.remove('dragging');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);

  // ── Init ─────────────────────────────────────────────────────────────
  updateGutter();
  updateTypeBadge();
  editor.focus();
  loadSchema();

  return function cleanup() {
    onMouseUp();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    delete window.sqRun;
    delete window.sqClear;
    delete window.sqExportCsv;
    delete window.sqRefreshSchema;
  };
}
